/**
 * MODULE: _shared/frontend-shell/src/hooks/assistant/streamSuggestedActions.ts
 * PURPOSE: Parse suggested_next_actions from orchestration SSE complete metadata.
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-07-08 [2.2.1481] — In-chat follow-up chips from action-first / company_research replies.
 */

export type StreamSuggestedAction = {
  label: string;
  prompt_text?: string;
  execution_mode?: string;
  action?: string;
  source?: string;
};

function normalizeStreamSuggestedAction(raw: unknown): StreamSuggestedAction | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const label = typeof row.label === 'string' ? row.label.trim() : '';
  const promptText =
    typeof row.prompt_text === 'string' && row.prompt_text.trim()
      ? row.prompt_text.trim()
      : label;
  if (!label && !promptText) return null;
  return {
    label: label || promptText,
    prompt_text: promptText,
    execution_mode:
      typeof row.execution_mode === 'string' ? row.execution_mode : undefined,
    action: typeof row.action === 'string' ? row.action : undefined,
    source: typeof row.source === 'string' ? row.source : undefined,
  };
}

/** Structured chips from complete.metadata.suggested_next_actions (preferred). */
export function parseStreamSuggestedActionsFromMetadata(
  metadata: unknown,
): StreamSuggestedAction[] {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return [];
  }
  const m = metadata as Record<string, unknown>;
  const structured = m.suggested_next_actions;
  if (Array.isArray(structured)) {
    const out: StreamSuggestedAction[] = [];
    const seen = new Set<string>();
    for (const row of structured) {
      const action = normalizeStreamSuggestedAction(row);
      if (!action) continue;
      const key = (action.prompt_text || action.label).toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(action);
    }
    return out;
  }
  const legacy = m.next_suggestions;
  if (!Array.isArray(legacy)) return [];
  const out: StreamSuggestedAction[] = [];
  const seen = new Set<string>();
  for (const item of legacy) {
    if (typeof item !== 'string') continue;
    const prompt = item.trim();
    const key = prompt.toLowerCase();
    if (!prompt || seen.has(key)) continue;
    seen.add(key);
    out.push({
      label: prompt.length <= 72 ? prompt : `${prompt.slice(0, 69)}…`,
      prompt_text: prompt,
      execution_mode: 'prompt',
      source: 'next_suggestions_legacy',
    });
  }
  return out;
}

export function streamSuggestedActionPrompts(
  actions: StreamSuggestedAction[] | null | undefined,
  limit = 3,
): string[] {
  if (!actions?.length) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const action of actions) {
    const text = (action.prompt_text || action.label || '').trim();
    const key = text.toLowerCase();
    if (!text || seen.has(key)) continue;
    seen.add(key);
    out.push(text);
    if (out.length >= limit) break;
  }
  return out;
}
